import { BlockListContainer, FoodsIcon, FoodsIllustrations, WhiteBox } from "@foodpilot/foods";
import { ActionBlock } from "./ActionBlock";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { PlanningLayout } from "../ActionListLayout";
import { useGetAllActionPlanActions } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { ActionType } from "./ActionsBlock/ActionRow";
import { ScoreService } from "src/context/EntityBasedScore/ScoreService";
import { useState } from "react";
import { ListPlanningHeader } from "./Header/Header";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Loading } from "src/components/utils/Loading";

type ActionBlock = {
    totalScore: number | null;
    actionBlocks: ActionType[];
};

export const ListPlanning = () => {
    const {
        areActionsLoading,
        actionPlan,
        actionsByCsrTheme,
        selectPostsByCampaigns,
        selectedActionPlan,
        getActionScoresByCsrTheme,
        selectedScore,
        isEditMode,
        toggleEditMode,
    } = useActionsContext();
    const {
        data: allActionPlanActions,
        isFetched,
        isRefetching,
    } = useGetAllActionPlanActions({
        actionPlanID: actionPlan?.id,
    });

    if (isRefetching === false) {
        if (isFetched === false) {
            return;
        }
    }

    const shouldFilterOutEmptyActions = isEditMode === false;
    const actionBlocks = Object.entries(actionsByCsrTheme).map(([csrTitle, actions], index) => {
        const groupActions =
            actions?.map((action) => {
                return {
                    csrTitle: csrTitle,
                    actionId: action.id,
                    title: action.title,
                };
            }) ?? [];

        const acc: { totalScore: number | null; actionBlocks: ActionType[] } = {
            actionBlocks: [],
            totalScore: null,
        };

        const actionBlocksWithTotal = groupActions.reduce((previousAcc, actionItem) => {
            const actionSsqPostsById = Object.groupBy(allActionPlanActions ?? [], (item) => item.actionId);
            const item = actionSsqPostsById?.[actionItem.actionId];
            const actionFound = item !== undefined && item.length > 0 ? item[0] : null;

            const startingCampaign = actionFound?.startSimulationSsqPost.campaign;
            const endingCampaign = actionFound?.endSimulationSsqPost.campaign;

            const postsToCompare = selectPostsByCampaigns(
                selectedActionPlan,
                startingCampaign?.id,
                endingCampaign?.id,
                true,
            );

            const getScore = () => {
                if (actionFound === null) return null;

                const director = getActionScoresByCsrTheme(postsToCompare, [actionItem.actionId]);

                const endScore = director?.end[actionItem.csrTitle]?.whitelistScore;
                const startScore = director?.start[actionItem.csrTitle]?.whitelistScore;

                const _actionScore = ScoreService.substractScore(startScore ?? null, endScore ?? null);
                const actionScore = ScoreService.normalizeScore(_actionScore, selectedScore?.precision ?? 0).value;
                const unit = actionScore === null || selectedScore?.unit === undefined ? "" : selectedScore.unit;

                return { actionScore, unit };
            };
            const { actionScore = null, unit = "" } = getScore() || {};

            const actionBlock: ActionType = {
                id: actionFound?.id ?? null,
                actionId: actionItem.actionId,
                dateLabel: actionFound === null ? null : `${startingCampaign?.label} - ${endingCampaign?.label}`,
                actionTitle: actionItem.title,
                value: actionScore,
                unit: unit,
                type: actionFound?.type,
            };
            if (shouldFilterOutEmptyActions) {
                if (actionBlock.id === null) {
                    return previousAcc;
                }
            }

            previousAcc["actionBlocks"].push(actionBlock);
            previousAcc["totalScore"] = ScoreService.addScore(previousAcc["totalScore"], actionScore);

            return previousAcc;
        }, acc);

        const blockData = { csrTitle, actionBlocksWithTotal };
        return blockData;
    });

    const blocks = actionBlocks.flatMap((i, index) => {
        const numberOfBlocks = i.actionBlocksWithTotal.actionBlocks.length;
        if (numberOfBlocks === 0) return [];

        return [
            <ActionBlock
                key={`actionList-${i.csrTitle}-${index}`}
                sectionTitle={i.csrTitle}
                score={i.actionBlocksWithTotal.totalScore}
                blocks={i.actionBlocksWithTotal.actionBlocks}
            />,
        ];
    });

    const emptyBlocks = [<EmptyBlock toggleEdit={toggleEditMode} />];
    const blocksToDisplay = blocks.length > 0 ? blocks : emptyBlocks;

    return (
        <PlanningLayout>
            {areActionsLoading === true ?
                <Loading />
            :   <BlockListContainer
                    header={<ListPlanningHeader isEdit={isEditMode} toggleEdit={toggleEditMode} />}
                    blocks={blocksToDisplay}
                />
            }
        </PlanningLayout>
    );
};

type EmptyBlockProps = {
    toggleEdit: () => void;
};
const EmptyBlock = (props: EmptyBlockProps) => {
    const { toggleEdit } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <WhiteBox>
            <Stack
                justifyContent={"center"}
                alignItems={"center"}
                padding={theme.spacing(6, 10, 6, 10)}
                gap={theme.spacing(3)}
            >
                <FoodsIllustrations illustration="defaultIllustration" />
                <Typography variant="h4">{t("action-plan.list.empty.title")}</Typography>
                <Typography variant="body-medium">{t("action-plan.list.empty.info")}</Typography>
                <Button variant="primary" startIcon={<FoodsIcon size={2} icon={"addRemove"} />} onClick={toggleEdit}>
                    {t("action-plan.list.empty.add-actions")}
                </Button>
            </Stack>
        </WhiteBox>
    );
};
